// Columna central del explorer de Cartera: las asignaciones crédito↔cobrador de la cadena.
// Cargando, error de lectura, «esta cadena no tiene asignaciones» y «ninguna pasa el filtro» son
// cuatro estados distintos: confundirlos hace que una lectura caída se lea como cartera vacía.
import { Badge, ErrorState, Loading } from '@/kit';
import { userMessageOf } from '@/api/errors';
import { AssignmentStatusEnum } from '@fiado/type-kit/bin/loanCollectorAssignment/enums/AssignmentStatusEnum';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import {
    describeAssignmentStatus,
    describeCollector,
    describeStore,
    sortAssignments,
} from '../logic';
import type { AssignmentRow } from '../types';

const CELL = 'border-b border-border/50 px-[3px] py-[5px] text-[11px] align-middle';
const HEAD =
    'px-[3px] py-[5px] text-left align-bottom text-[9.5px] font-bold uppercase leading-tight tracking-[0.04em] text-muted-foreground';

const COLUMNS = [
    { key: 'credit', label: 'Crédito', width: 'w-[20%]' },
    { key: 'collector', label: 'Cobrador', width: 'w-[20%]' },
    { key: 'store', label: 'Tienda', width: 'w-[17%]' },
    { key: 'status', label: 'Estado', width: 'w-[13%]' },
    { key: 'assignedAt', label: 'Asignado el', width: 'w-[16%]' },
    { key: 'reassignedFrom', label: 'Reasignada de', width: 'w-[14%]' },
];

const STATUS_VARIANT: Record<AssignmentStatusEnum, 'success' | 'warning' | 'secondary'> = {
    [AssignmentStatusEnum.ACTIVE]: 'success',
    [AssignmentStatusEnum.REASSIGNED]: 'warning',
    [AssignmentStatusEnum.INACTIVE]: 'secondary',
};

/** Nombre resuelto, o el id en mono con la leyenda de por qué no hay nombre. */
function ResolvedCell({
    text,
    note,
    isName,
}: {
    text: string;
    note: string | null;
    isName: boolean;
}) {
    return (
        <>
            <div className={cn('truncate', !isName && 'font-mono text-[10.5px]')} title={text}>
                {text}
            </div>
            {note === null ? null : (
                <div className="truncate text-[9.5px] italic text-muted-foreground">{note}</div>
            )}
        </>
    );
}

export interface AssignmentsTableProps {
    /** Filas YA filtradas; el orden lo resuelve `sortAssignments`. */
    rows: readonly AssignmentRow[];
    /** Cuántas filas trajo la lectura antes de filtrar: separa «no hay» de «el filtro no deja pasar». */
    totalRows: number;
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
    selectedAssignmentId: string | null;
    onSelect: (assignmentId: string) => void;
    className?: string;
}

export function AssignmentsTable({
    rows,
    totalRows,
    isLoading,
    error,
    onRetry,
    selectedAssignmentId,
    onSelect,
    className,
}: AssignmentsTableProps) {
    if (isLoading) return <Loading text="Cargando asignaciones…" />;

    // `ErrorState` ya trae `role="alert"`; una lectura caída nunca se pinta como lista vacía.
    if (error) {
        return (
            <ErrorState
                className="m-3"
                title="No se pudieron cargar las asignaciones"
                description={userMessageOf(error)}
                onRetry={onRetry}
            />
        );
    }

    const ordered = sortAssignments(rows);

    return (
        <div className={cn('min-h-0 flex-1 overflow-auto', className)}>
            <table className="w-full min-w-[460px] table-fixed border-collapse">
                <caption className="sr-only">
                    Asignaciones de crédito a cobrador de la cadena, con su estado y su origen.
                </caption>
                <thead className="sticky top-0 z-10 bg-muted">
                    <tr className="border-b border-border">
                        {COLUMNS.map((column) => (
                            <th key={column.key} scope="col" className={cn(HEAD, column.width)}>
                                {column.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {ordered.length === 0 ? (
                        <tr>
                            <td
                                colSpan={COLUMNS.length}
                                className="px-2 py-6 text-center text-[12px] text-muted-foreground"
                            >
                                {totalRows === 0
                                    ? 'Esta cadena todavía no tiene asignaciones de cobranza.'
                                    : 'Ninguna asignación pasa estos filtros.'}
                            </td>
                        </tr>
                    ) : (
                        ordered.map((row) => {
                            const collector = describeCollector(
                                row.collectorUserId,
                                row.collectorDisplayName,
                                row.collectorPresence,
                            );
                            const store = describeStore(row.storeId, row.storeName);
                            const selected = row.assignmentId === selectedAssignmentId;
                            return (
                                <tr
                                    key={row.assignmentId}
                                    aria-selected={selected}
                                    onClick={() => {
                                        onSelect(row.assignmentId);
                                    }}
                                    className={cn(
                                        'cursor-pointer transition-colors',
                                        selected
                                            ? 'bg-success-soft/55 shadow-[inset_3px_0_0_hsl(var(--success))] hover:bg-success-soft/70'
                                            : 'hover:bg-accent/40',
                                    )}
                                >
                                    <td className={cn(CELL, 'truncate font-mono text-[10.5px]')}>
                                        {/* El contrato no trae referencia legible del crédito: el id ES lo único que lo identifica. */}
                                        {/* eslint-disable-next-line no-restricted-syntax */}
                                        {row.creditId}
                                    </td>
                                    <td className={cn(CELL, 'truncate')}>
                                        <ResolvedCell {...collector} />
                                    </td>
                                    <td className={cn(CELL, 'truncate')}>
                                        <ResolvedCell {...store} />
                                    </td>
                                    <td className={CELL}>
                                        <Badge variant={STATUS_VARIANT[row.status]}>
                                            {describeAssignmentStatus(row.status)}
                                        </Badge>
                                    </td>
                                    <td className={cn(CELL, 'truncate tabular-nums')}>
                                        {formatDateTimeMx(row.assignedAt)}
                                    </td>
                                    <td className={cn(CELL, 'truncate')}>
                                        {row.reassignedFromUserId === null ? (
                                            <span className="text-muted-foreground">
                                                No viene de otro cobrador
                                            </span>
                                        ) : (
                                            <ResolvedCell
                                                {...describeCollector(
                                                    row.reassignedFromUserId,
                                                    row.reassignedFromDisplayName,
                                                    row.reassignedFromPresence,
                                                )}
                                            />
                                        )}
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
}
